import {inject, Injectable} from '@angular/core';
import {BehaviorSubject} from 'rxjs';
import {IConversationFilterParam} from '../models/conversation.model';
import {Params, Router} from '@angular/router';

/**
 * FILE: conversation-filter.service.ts
 * CHỨC NĂNG: Quản lý tham số bộ lọc cho danh sách hội thoại + đồng bộ URL
 * TÁC DỤNG:
 * - State params$ để các component subscribe
 * - setParams/getCurrent để cập nhật và lấy cấu hình hiện tại
 * - Đồng bộ Router: Cập nhật URL khi lọc và nạp lại khi F5 (copy link, deep link)
 * - Whitelist + rename + value mapping (giống FE13 FilterConversationService)
 */
@Injectable({providedIn: 'root'})
export class ConversationFilterService {
  private _params$ = new BehaviorSubject<IConversationFilterParam>({});
  /** Observable chứa tham số lọc hiện tại */
  public params$ = this._params$.asObservable();

  private readonly router = inject(Router);

  // ─── Whitelist: các param được phép đồng bộ lên URL (FE13 filter-conversation.service.ts) ───
  private readonly PARAMS = [
    'filterPageIds', 'type', 'status', 'isUnread', 'fromAds', 'hasPhone',
    'hasReply', 'hasDraftOrder', 'updatedTime', 'createdTime', 'tagIds',
    'noTag', 'totalTag', 'postIds', 'postId', 'assigneeIds',
    'divisionStaffId', 'assignedGroupId', 'customerName', 'typeSearch', 'searchContent',
    'cid', 'psid', 'isSentPrivateMessage', 'isCreatedOrder', 'isFromLive',
    'adIds', 'chatbotStatus', 'sort', 'reportFilter', 'newConversation', 'oldConversation',
    'interactTime', 'interactGroup', 'interactByPage', 'pagePhone', 'pagePhoneTime', 'pagePhoneHour',
  ];

  /** Các params dạng mảng, cần join/split bằng dấu phẩy trên URL */
  private readonly PARAMS_TYPE_ARRAY = [
    'filterPageIds', 'tagIds', 'postIds', 'assigneeIds', 'assignedGroupId', 'adIds',
  ];

  /** Map rút gọn tên param trên URL: filterPageIds → pageIds (FE13 parity) */
  private readonly PARAMS_RENAME_MAP: Record<string, string> = {
    filterPageIds: 'pageIds',
    liveDialog: 'postId', // Special case: từ live reply dialog
  };

  /** Map giá trị trước khi set lên URL: boolean → number (FE13 parity) */
  private readonly PARAMS_VALUE_MAP: Record<string, Record<string, any>> = {
    hasPhone: {true: 1, false: -1},
    hasReply: {true: 1, false: -1},
    hasDraftOrder: {true: 1},
    fromAds: {true: 1},
  };

  // ─── Helper methods ─────────────────────────────────────────────────────────

  private isWhitelisted(key: string): boolean {
    return this.PARAMS.includes(key);
  }

  /** Đổi tên param code → URL (filterPageIds → pageIds) */
  private renameKeyForUrl(key: string): string {
    return this.PARAMS_RENAME_MAP[key] || key;
  }

  /** Đổi tên param URL → code (pageIds → filterPageIds) */
  private renameKeyForCode(key: string): string {
    const entry = Object.entries(this.PARAMS_RENAME_MAP).find(([_, v]) => v === key);
    return entry ? entry[0] : key;
  }

  /** Map giá trị trước khi set lên URL (true → 1) */
  private mapValueForUrl(key: string, value: any): any {
    const mapper = this.PARAMS_VALUE_MAP[key];
    if (mapper && mapper[value] !== undefined) {
      return mapper[value];
    }
    return value;
  }

  /** Map giá trị từ URL về code (1 → true) */
  private mapValueForCode(key: string, value: any): any {
    const mapper = this.PARAMS_VALUE_MAP[key];
    if (mapper) {
      const entry = Object.entries(mapper).find(([_, v]) => String(v) === String(value));
      if (entry) {
        return entry[0] === 'true' ? true : (entry[0] === 'false' ? false : entry[0]);
      }
    }
    return value;
  }

  /** Kiểm tra param có phải dạng mảng (cần parse comma-separated) */
  private isArrayType(key: string): boolean {
    return this.PARAMS_TYPE_ARRAY.includes(key);
  }

  // ─── Public API ─────────────────────────────────────────────────────────────

  /**
   * Cập nhật thông số lọc và đồng bộ lên URL.
   * @param params IConversationFilterParam
   * @param merge Nếu true (mặc định), sẽ ghi đè các field mới vào filter cũ.
   */
  public setParams(params: IConversationFilterParam, merge = true): void {
    const next: IConversationFilterParam = merge ? {...this._params$.getValue(), ...params} : {...params};

    // Xóa các key có giá trị null/undefined để clean request
    Object.keys(next).forEach(key => {
      const value = (next as any)[key];
      if (value === null || value === undefined || value === '') {
        delete (next as any)[key];
      }
    });

    this._params$.next(next);
    this.syncToUrl(next);
  }

  /**
   * Cập nhật params mà KHÔNG emit trên params$ (không trigger fetch).
   * Dùng khi cần clear filter state (vd: xoá cid khi back) mà không muốn reload list.
   */
  public setParamsSilent(params: IConversationFilterParam): void {
    const next: IConversationFilterParam = {...this._params$.getValue(), ...params};
    Object.keys(next).forEach(key => {
      const value = (next as any)[key];
      if (value === null || value === undefined || value === '') {
        delete (next as any)[key];
      }
    });
    // Cập nhật giá trị nội bộ — KHÔNG gọi next() → subscription không fire
    (this._params$ as any)._value = next;
  }

  /**
   * Đồng bộ object params hiện tại lên URL Query Params.
   * Chỉ sync các param trong whitelist, có rename + value mapping.
   * FE13 parity: filter-conversation.service.ts setSnapShotQueryParams()
   */
  private syncToUrl(params: IConversationFilterParam): void {
    const queryParams: Params = {};

    for (const [key, value] of Object.entries(params)) {
      // Chỉ sync các param trong whitelist
      if (!this.isWhitelisted(key)) continue;
      // Bỏ qua null / undefined / empty
      if (value === null || value === undefined || value === '') continue;
      if (Array.isArray(value) && value.length === 0) continue;

      const renamedKey = this.renameKeyForUrl(key);

      if (Array.isArray(value)) {
        // Mảng → chuỗi phẩy (vd: pageIds=id1,id2,id3)
        queryParams[renamedKey] = value.join(',');
      } else {
        // Map giá trị (vd: hasPhone=true → hasPhone=1)
        queryParams[renamedKey] = this.mapValueForUrl(key, value);
      }
    }

    this.router.navigate([], {
      queryParams,
      replaceUrl: true, // Không tạo thêm history entry mỗi khi bấm lọc
    });
  }

  /**
   * Đọc dữ liệu từ URL để khởi tạo bộ lọc (Dùng khi F5 hoặc Copy link)
   * FE13 parity: filter-conversation.service.ts getSnapShotQueryParams()
   */
  public parseParamsFromUrl(queryParams: Params): void {
    const params: IConversationFilterParam = {};

    // Helper để parse mảng từ string phẩy
    const parseArray = (val: any) => (typeof val === 'string' ? val.split(',') : (Array.isArray(val) ? val : []));

    // Map URL key → code key, filter theo whitelist
    const p: Record<string, string> = {};
    for (const [key, value] of Object.entries(queryParams)) {
      const codeKey = this.renameKeyForCode(key);
      if (!this.isWhitelisted(codeKey)) continue;
      p[codeKey] = String(value);
    }

    // --- Number params ---
    if (p['type']) params.type = Number(p['type']);
    if (p['status']) params.status = Number(p['status']);
    if (p['sort']) params.sort = Number(p['sort']);
    if (p['noTag']) params.noTag = Number(p['noTag']);
    if (p['totalTag']) params.totalTag = Number(p['totalTag']);
    if (p['isSentPrivateMessage']) params.isSentPrivateMessage = Number(p['isSentPrivateMessage']);
    if (p['isCreatedOrder']) params.isCreatedOrder = Number(p['isCreatedOrder']);
    if (p['isFromLive']) params.isFromLive = Number(p['isFromLive']);
    if (p['interactByPage']) params.interactByPage = Number(p['interactByPage']);

    // --- Boolean params (hỗ trợ cả 'true'/'false' và '1'/'0' cho FE13 URL cũ) ---
    if (p['isUnread']) params.isUnread = p['isUnread'] === 'true' || p['isUnread'] === '1';
    if (p['fromAds']) params.fromAds = p['fromAds'] === 'true' || p['fromAds'] === '1';
    if (p['hasPhone']) params.hasPhone = p['hasPhone'] === 'true' || p['hasPhone'] === '1';
    if (p['hasReply']) params.hasReply = p['hasReply'] === 'true' || p['hasReply'] === '1';
    if (p['hasDraftOrder']) params.hasDraftOrder = p['hasDraftOrder'] === 'true' || p['hasDraftOrder'] === '1';
    if (p['reportFilter']) params.reportFilter = p['reportFilter'] === 'true' || p['reportFilter'] === '1';
    if (p['newConversation']) params.newConversation = p['newConversation'] === 'true' || p['newConversation'] === '1';
    if (p['oldConversation']) params.oldConversation = p['oldConversation'] === 'true' || p['oldConversation'] === '1';

    // --- Array params (comma-separated on URL) ---
    if (p['tagIds']) params.tagIds = parseArray(p['tagIds']);
    if (p['filterPageIds']) params.filterPageIds = parseArray(p['filterPageIds']);
    if (p['postIds']) params.postIds = parseArray(p['postIds']);
    if (p['assigneeIds']) params.assigneeIds = parseArray(p['assigneeIds']);
    if (p['adIds']) params.adIds = parseArray(p['adIds']);
    if (p['assignedGroupId']) params.assignedGroupId = p['assignedGroupId'];

    // --- String params ---
    if (p['cid']) params.cid = p['cid'];
    if (p['psid']) params.psid = p['psid'];
    if (p['divisionStaffId']) params.divisionStaffId = p['divisionStaffId'];
    if (p['chatbotStatus']) params.chatbotStatus = p['chatbotStatus'];
    if (p['customerName']) params.customerName = p['customerName'];
    if (p['typeSearch']) params.typeSearch = Number(p['typeSearch']) as IConversationFilterParam['typeSearch'];
    if (p['searchContent']) params.searchContent = p['searchContent'];
    if (p['interactGroup']) params.interactGroup = p['interactGroup'];

    // --- Time range params (FE13: "fromUnix,toUnix") ---
    if (p['updatedTime']) params.updatedTime = p['updatedTime'];
    if (p['createdTime']) params.createdTime = p['createdTime'];
    if (p['interactTime']) params.interactTime = p['interactTime'];
    if (p['pagePhone']) params.pagePhone = p['pagePhone'];
    if (p['pagePhoneTime']) params.pagePhoneTime = p['pagePhoneTime'];
    if (p['pagePhoneHour']) params.pagePhoneHour = p['pagePhoneHour'];

    // Cập nhật state (không gọi syncToUrl để tránh loop)
    if (Object.keys(params).length > 0) {
      this._params$.next({...this._params$.getValue(), ...params});
    }
  }

  /**
   * Trả về cấu hình lọc hiện thời (dạng object thuần)
   */
  public getCurrent(): IConversationFilterParam {
    return this._params$.getValue();
  }

  /**
   * Reset bộ lọc về mặc định (rỗng)
   */
  public reset(): void {
    this._params$.next({});
    this.syncToUrl({});
  }

  /**
   * Áp dụng bộ lọc nhanh và xóa các filter cũ để tránh xung đột logic (giống bản cũ)
   */
  public applyQuickFilter(type: string): void {
    switch (type) {
      case 'all':
        this.reset();
        break;
      case 'unread':
        this.setParams({isUnread: true}, false); // false để replace hoàn toàn
        break;
      case 'phone':
        this.setParams({hasPhone: true}, false);
        break;
      case 'draft':
        this.setParams({hasDraftOrder: true}, false);
        break;
    }
  }
}
